/**
 * Selects the configuration source for a new workspace agent.
 *
 * Templates clone an existing reviewed setup while global administrators may
 * start from an empty agent. This component exposes that product choice without
 * owning creation, authorization, or template-copy behavior.
 */
import { Bot, CheckCircle2, Plus } from 'lucide-react'

import { Input } from '@/components/ui'
import type { AgentTemplate } from '@/subsystems/agents'
import { slugify } from '@/subsystems/layout/utils/layout-utils'

interface AgentTypePickerProps {
  templates: AgentTemplate[]
  selectedTemplate: AgentTemplate | null
  customSelected: boolean
  isGlobalAdmin: boolean
  name: string
  onNameChange: (value: string) => void
  onSelectTemplate: (template: AgentTemplate) => void
  onSelectCustom: () => void
}

function SelectionCard({
  selected,
  label,
  description,
  index,
  custom = false,
  testId,
  onClick,
}: {
  selected: boolean
  label: string
  description?: string | null
  index: number
  custom?: boolean
  testId: string
  onClick: () => void
}) {
  const Icon = custom ? Plus : Bot
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{ animationDelay: `${index * 45}ms` }}
      className={`docreview-agent-option ${selected ? 'docreview-agent-option-selected border-[#93c5fd] bg-[#eff6ff]' : 'border-[#e2e8f0] bg-white'} flex w-full items-start gap-3.5 rounded-[11px] border-[1.5px] px-[18px] py-4 text-left focus:outline-none focus:ring-2 focus:ring-[#94a3b8] focus:ring-offset-2`}
    >
      <span className={`mt-px flex size-[38px] shrink-0 items-center justify-center rounded-[10px] ${selected ? 'bg-[#dbeafe] text-[#2563eb]' : 'bg-[#f1f5f9] text-[#475569]'}`}>
        <Icon className="size-[19px]" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="truncate text-[15.5px] font-semibold leading-[1.3] text-[#0f172a]">{label}</span>
        {description && <span className="line-clamp-3 text-[13.5px] leading-[1.5] text-[#64748b]">{description}</span>}
      </span>
      {selected && <CheckCircle2 data-testid={`${testId}-selected-check`} className="docreview-pop mt-[9px] size-5 shrink-0 text-[#2563eb]" strokeWidth={2.5} />}
    </button>
  )
}

export function AgentTypePicker(props: AgentTypePickerProps) {
  return (
    <div className="flex flex-col gap-2.5">
      {props.templates.map((template, index) => (
        <SelectionCard
          key={template.agentId}
          selected={props.selectedTemplate?.agentId === template.agentId}
          label={template.label}
          description={template.description}
          index={index}
          testId={`agent-template-card-${template.agentId}`}
          onClick={() => props.onSelectTemplate(template)}
        />
      ))}
      {props.isGlobalAdmin && (
        <>
          <div className="mx-1 my-0.5 h-px bg-[#eef2f6]" />
          <SelectionCard
            selected={props.customSelected}
            label="Create a Custom Agent"
            description="Configure pipeline, knowledge bases, and renderers from scratch."
            index={props.templates.length}
            custom
            testId="custom-agent-card"
            onClick={props.onSelectCustom}
          />
        </>
      )}
      {props.selectedTemplate && (
        <div className="docreview-row mx-1 mt-1 flex flex-col gap-2 border-t border-[#eef2f6] px-0.5 pb-1.5 pt-4">
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="create-template-agent-name" className="text-sm font-semibold text-[#0f172a]">Agent Name</label>
            <span className="truncate font-mono text-xs text-[#94a3b8]">agents/<span className="font-semibold text-[#2563eb]">{slugify(props.name) || 'my-agent'}</span></span>
          </div>
          <Input
            id="create-template-agent-name"
            value={props.name}
            onChange={event => props.onNameChange(event.target.value)}
            placeholder={props.selectedTemplate.label}
            className="h-11 rounded-[9px] border-[#d1d5db] px-3.5 !text-[15px] shadow-[0_1px_2px_rgba(0,0,0,.04)] focus-visible:!border-[#93c5fd] focus-visible:!ring-[rgba(37,99,235,.08)]"
          />
          <p className="text-[12.5px] leading-[1.5] text-[#94a3b8]">
            Starts with everything from <span className="font-semibold text-[#475569]">{props.selectedTemplate.label}</span> — knowledge base, prompts, and renderers. You can reconfigure it later.
          </p>
        </div>
      )}
    </div>
  )
}
